/**
 * Automations page — shows all scheduled tasks across all teammates in one place.
 * Combines data from useTasks, useSchedules, useRecentRuns, and useAgents to display
 * only tasks that have active schedules, with filtering/sorting via useTeammateUIStore.
 */
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlarmClock } from "@/components/animate-ui/icons/alarm-clock";
import { Plus } from "@/components/animate-ui/icons/plus";
import {
	type ScheduleConfig,
	TaskConfirmationModal,
	type TaskFormData,
} from "@/components/task-confirmation-modal";
import { generateCronExpression } from "@/components/task-creation-form/schedule-utils";
import { TaskFilters } from "@/components/teammate/task-filters";
import { TaskList } from "@/components/teammate/task-list";
import { TaskRunPanel } from "@/components/teammate/task-run-panel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { getQueryClient } from "@/integrations/tanstack-query/root-provider";
import { authClient } from "@/lib/api/auth-client";
import {
	useAgents,
	useCreateSchedule,
	useDeleteSchedule,
	useRecentRuns,
	useRunTask,
	useSchedules,
	useTasks,
	useUpdateTask,
} from "@/lib/api/hooks/tasks";
import type { components } from "@/lib/api/types";
import { buildMetaTags } from "@/lib/seo/meta";
import { buildScheduleParams } from "@/lib/utils/schedule";
import { useTeammateUIStore } from "@/stores/teammate-ui-store";

export const Route = createFileRoute("/_dashboard/automations")({
	head: () => ({
		meta: buildMetaTags({
			title: "Automations",
			description: "Manage all your scheduled tasks in one place",
			noIndex: true,
			noFollow: true,
		}),
	}),
	component: AutomationsPage,
});

function AutomationsPage() {
	const navigate = useNavigate();

	// Data queries
	const { data: allTasks, isLoading: isLoadingTasks } = useTasks();
	const { data: schedulesData, isLoading: isLoadingSchedules } = useSchedules();
	const schedules = schedulesData?.schedules || [];
	const { data: agents } = useAgents();
	const { data: recentRuns = [] } = useRecentRuns(100);

	// UI store
	const {
		searchQuery,
		statusFilter,
		typeFilter,
		sortOption,
		selectedTaskId,
		isPanelOpen,
		setSelectedTask,
		closePanel,
		setSortOption,
	} = useTeammateUIStore();

	// Default to next-run sort on this page; restore last-run on unmount
	useEffect(() => {
		setSortOption("next-run");
		return () => setSortOption("last-run");
	}, [setSortOption]);

	// Modal state
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
	const [taskInstructions, setTaskInstructions] = useState("");
	const [defaultName, setDefaultName] = useState("");
	const [defaultGoals, setDefaultGoals] = useState("");
	const [defaultIntegrations, setDefaultIntegrations] = useState<number[]>([]);
	const [defaultSchedule, setDefaultSchedule] = useState<ScheduleConfig | undefined>(undefined);
	const [defaultPermissionMode, setDefaultPermissionMode] = useState<"yolo" | "safe" | "plan">(
		"safe"
	);

	// Mutations
	const updateTask = useUpdateTask(editingTaskId || 0);
	const createSchedule = useCreateSchedule();
	const deleteSchedule = useDeleteSchedule();

	const runTask = useRunTask();

	// Quick-create mutation for new automations
	const quickCreate = useMutation({
		mutationFn: async (body: components["schemas"]["QuickCreateTaskRequest"]) => {
			const res = await authClient.POST("/api/v1/tasks/quick-create", { body });
			if (res.error) {
				const msg = Array.isArray(res.error.detail)
					? res.error.detail.map((e) => e.msg).join(", ")
					: (res.error.detail as string | undefined) || "Failed to create automation";
				throw new Error(msg);
			}
			return res.data;
		},
		onSuccess: (data) => {
			getQueryClient()?.invalidateQueries({ queryKey: ["agents"] });
			getQueryClient()?.invalidateQueries({ queryKey: ["tasks"] });
			getQueryClient()?.invalidateQueries({ queryKey: ["schedules"] });
			setIsModalOpen(false);
			toast.success("Automation created!", { description: data?.task?.name });
		},
		onError: (error: Error) => {
			toast.error("Failed to create automation", { description: error.message });
		},
	});

	// Build set of task IDs that have schedules
	const scheduledTaskIds = useMemo(() => new Set(schedules.map((s) => s.task_id)), [schedules]);

	// Map agent IDs → names for displaying on task cards
	const agentNameMap = useMemo(() => new Map(agents?.map((a) => [a.id, a.name]) ?? []), [agents]);

	// Filter to only scheduled tasks, then apply search/status/sort
	const tasks = useMemo(() => {
		if (!allTasks) return [];

		// Only tasks with active schedules
		let filtered = allTasks.filter((t) => scheduledTaskIds.has(t.id));

		// Search
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter(
				(t) => t.name.toLowerCase().includes(query) || t.instructions?.toLowerCase().includes(query)
			);
		}

		// Status filter (UI uses "paused" but API status is "disabled")
		if (statusFilter !== "all") {
			const mappedFilter = statusFilter === "paused" ? "disabled" : statusFilter;
			filtered = filtered.filter((t) => t.status.toLowerCase() === mappedFilter);
		}

		// Type filter (all automations are scheduled, so only "scheduled" and "all" match)
		if (typeFilter !== "all" && typeFilter !== "scheduled") {
			return [];
		}

		// Sort
		return [...filtered].sort((a, b) => {
			switch (sortOption) {
				case "name-az":
					return a.name.localeCompare(b.name);
				case "name-za":
					return b.name.localeCompare(a.name);
				case "last-run":
					return (
						(b.updated_at ? new Date(b.updated_at).getTime() : 0) -
						(a.updated_at ? new Date(a.updated_at).getTime() : 0)
					);
				case "next-run": {
					const getNextRun = (id: number) => {
						const s = schedules.find((s) => s.task_id === id);
						return s?.next_run_time ? new Date(s.next_run_time).getTime() : Number.MAX_SAFE_INTEGER;
					};
					return getNextRun(a.id) - getNextRun(b.id);
				}
				default:
					return 0;
			}
		});
	}, [allTasks, scheduledTaskIds, schedules, searchQuery, statusFilter, typeFilter, sortOption]);

	// Build latestRunByTaskId from recent runs
	const latestRunByTaskId = useMemo(() => {
		const map = new Map<number, components["schemas"]["RunResponse"]>();
		for (const run of recentRuns) {
			const taskId = run.task_id;
			if (typeof taskId !== "number") continue;
			const existing = map.get(taskId);
			if (!existing) {
				map.set(taskId, run);
				continue;
			}
			const existingTime = existing.created_at ? new Date(existing.created_at).getTime() : NaN;
			const runTime = run.created_at ? new Date(run.created_at).getTime() : NaN;
			if (!Number.isNaN(runTime) && (Number.isNaN(existingTime) || runTime > existingTime)) {
				map.set(taskId, run);
			}
		}
		return map;
	}, [recentRuns]);

	// Find the agent for a given task (needed for submit handler)
	const getAgentForTask = (taskOrAgentId: number) => agents?.find((a) => a.id === taskOrAgentId);

	// Open modal in create mode with "On a Schedule" default
	const handleNewAutomation = () => {
		setEditingTaskId(null);
		setTaskInstructions("");
		setDefaultName("");
		setDefaultGoals("");
		setDefaultIntegrations([]);
		setDefaultPermissionMode("safe");
		setDefaultSchedule({
			triggerType: "cron",
			cronExpression: "0 9 * * *",
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});
		setIsModalOpen(true);
	};

	// Handle schedule task — opens modal with schedule pre-selected
	const handleScheduleTask = (taskId: number) => {
		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) return;

		const agent = getAgentForTask(task.agent_instance_id);
		if (agent?.status !== "enabled") {
			toast.error("Cannot schedule tasks for disabled or archived teammates");
			return;
		}

		setEditingTaskId(taskId);
		setTaskInstructions(task.instructions);
		setDefaultName(task.name);
		setDefaultGoals(task.goals || "");
		setDefaultIntegrations(task.integration_ids || []);
		setDefaultPermissionMode((task.permission_mode as "yolo" | "safe" | "plan") || "yolo");

		const existingSchedule = schedules.find((s) => s.task_id === taskId);
		if (existingSchedule) {
			setDefaultSchedule({
				triggerType: existingSchedule.trigger_type === "interval" ? "interval" : "cron",
				intervalSeconds: existingSchedule.interval_seconds ?? undefined,
				cronExpression: existingSchedule.cron_expression ?? undefined,
				timezone: existingSchedule.timezone ?? "UTC",
			});
		} else {
			setDefaultSchedule({
				triggerType: "cron",
				cronExpression: "0 9 * * *",
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});
		}

		setIsModalOpen(true);
	};

	const handleEditTask = (taskId: number) => {
		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) return;

		const agent = getAgentForTask(task.agent_instance_id);
		if (agent?.status !== "enabled") {
			toast.error("Cannot edit tasks for disabled or archived teammates");
			return;
		}

		setEditingTaskId(taskId);
		setTaskInstructions(task.instructions);
		setDefaultName(task.name);
		setDefaultGoals(task.goals || "");
		setDefaultIntegrations(task.integration_ids || []);
		setDefaultPermissionMode((task.permission_mode as "yolo" | "safe" | "plan") || "yolo");

		const existingSchedule = schedules.find((s) => s.task_id === taskId);
		if (existingSchedule) {
			setDefaultSchedule({
				triggerType: existingSchedule.trigger_type === "interval" ? "interval" : "cron",
				intervalSeconds: existingSchedule.interval_seconds ?? undefined,
				cronExpression: existingSchedule.cron_expression ?? undefined,
				timezone: existingSchedule.timezone ?? "UTC",
			});
		} else {
			setDefaultSchedule(undefined);
		}

		setIsModalOpen(true);
	};

	const handleRunTask = async (taskId: number) => {
		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) {
			toast.error("Task not found");
			return;
		}

		const agent = getAgentForTask(task.agent_instance_id);
		if (agent?.status !== "enabled") {
			toast.error("Cannot run tasks for disabled or archived teammates");
			return;
		}

		if (task.status !== "enabled") {
			toast.error("Cannot run paused or archived tasks");
			return;
		}

		const toastId = `run-task-${taskId}`;
		toast.loading("Starting task...", { id: toastId, description: task.name });

		runTask.mutate(taskId, {
			onSuccess: ({ runId }) => {
				toast.success("Task is running", {
					id: toastId,
					description: task.name,
					action: {
						label: "View run",
						onClick: () => navigate({ to: "/runs/$runId", params: { runId: String(runId) } }),
					},
					duration: 10000,
				});
			},
			onError: (error) => {
				toast.error("Failed to start task", { id: toastId, description: error.message });
			},
		});
	};

	const handleSubmitTask = async (formData: TaskFormData) => {
		// Create mode — use quick-create API
		if (!editingTaskId) {
			let scheduleFields = {};
			if (formData.schedule === "interval") {
				const intervalSeconds =
					formData.intervalUnit === "hours"
						? (formData.intervalMinutes ?? 30) * 3600
						: (formData.intervalMinutes ?? 30) * 60;
				scheduleFields = { interval_seconds: intervalSeconds };
			} else if (
				formData.schedule === "daily" ||
				formData.schedule === "weekly" ||
				formData.schedule === "monthly"
			) {
				const cronExpression = generateCronExpression(formData);
				if (cronExpression) {
					scheduleFields = {
						cron_expression: cronExpression,
						timezone: formData.timezone ?? "America/Los_Angeles",
					};
				}
			}

			quickCreate.mutate({
				instructions: formData.instructions,
				expected_output: null,
				goals: formData.goals || null,
				integration_ids: formData.integrations,
				email_notifications: formData.emailNotifications,
				inbound_email_enabled: formData.inboundEmailEnabled,
				permission_mode: formData.permissionMode,
				schedule: formData.schedule,
				timezone: formData.timezone ?? "America/Los_Angeles",
				...scheduleFields,
			});
			return;
		}

		const task = allTasks?.find((t) => t.id === editingTaskId);
		const agentInstanceId = task?.agent_instance_id;
		if (!agentInstanceId) return;

		const agent = getAgentForTask(agentInstanceId);
		if (agent?.status !== "enabled") {
			toast.error("Cannot update tasks for disabled or archived teammates");
			return;
		}

		try {
			await updateTask.mutateAsync({
				name: formData.name,
				instructions: formData.instructions,
				expected_output: formData.expectedOutput || null,
				goals: formData.goals || null,
				integration_ids: formData.integrations.length > 0 ? formData.integrations : null,
				email_notifications: formData.emailNotifications,
				permission_mode: formData.permissionMode,
			});
			const taskId = editingTaskId;

			// Delete old schedule if it exists (handles both changes and removal via "Run now")
			if (editingTaskId) {
				const existingSchedule = schedules.find((s) => s.task_id === editingTaskId);
				if (existingSchedule) {
					const scheduleId = Number.parseInt(existingSchedule.job_id.replace("schedule_", ""), 10);
					await deleteSchedule.mutateAsync(scheduleId);
				}
			}

			if (formData.schedule === "now") {
				setIsModalOpen(false);
				toast.success("Task updated successfully!");
			} else {
				const { cronExpression, intervalSeconds } = buildScheduleParams(formData);

				await createSchedule.mutateAsync({
					task_id: taskId,
					agent_instance_id: agentInstanceId,
					cron_expression: cronExpression,
					interval_seconds: intervalSeconds,
					run_date: null,
					timezone: formData.timezone || "UTC",
				});

				setIsModalOpen(false);
				toast.success("Task updated successfully!");
			}

			setEditingTaskId(null);
		} catch (error) {
			console.error("Failed to save task:", error);
			toast.error("Failed to save task. Please try again.");
		}
	};

	// Loading
	if (isLoadingTasks || isLoadingSchedules) {
		return (
			<div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
				<div className="space-y-4 sm:space-y-6">
					<Skeleton className="h-8 w-48" />
					<div className="space-y-3">
						<Skeleton className="h-24 w-full" />
						<Skeleton className="h-24 w-full" />
						<Skeleton className="h-24 w-full" />
					</div>
				</div>
			</div>
		);
	}

	const hasScheduledTasks = allTasks?.some((t) => scheduledTaskIds.has(t.id)) ?? false;

	return (
		<div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
			<div className="space-y-4 sm:space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div>
						<h1 className="text-2xl font-bold tracking-tight">Automations</h1>
						<p className="text-muted-foreground text-sm mt-1">Run scheduled tasks on autopilot</p>
					</div>
					<Button onClick={handleNewAutomation}>
						<Plus animateOnHover className="h-4 w-4 mr-2" />
						New automation
					</Button>
				</div>

				{/* Content */}
				{hasScheduledTasks ? (
					<Card className="p-4 sm:p-6">
						<div className="space-y-3 sm:space-y-4">
							<TaskFilters />
							<TaskList
								tasks={tasks}
								onEdit={handleEditTask}
								onSelect={setSelectedTask}
								onRunNow={handleRunTask}
								onSchedule={handleScheduleTask}
								onViewTeammate={(id) =>
									navigate({ to: "/teammates/$id", params: { id: String(id) } })
								}
								latestRunByTaskId={latestRunByTaskId}
								agentNameMap={agentNameMap}
							/>
						</div>
					</Card>
				) : (
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant="icon">
								<AlarmClock />
							</EmptyMedia>
							<EmptyTitle>No automations yet</EmptyTitle>
							<EmptyDescription>
								Schedule tasks to run automatically. Create a new task with a schedule to get
								started.
							</EmptyDescription>
						</EmptyHeader>
						<EmptyContent>
							<Button onClick={handleNewAutomation}>
								<Plus animateOnHover className="h-4 w-4 mr-2" />
								New automation
							</Button>
						</EmptyContent>
					</Empty>
				)}
			</div>

			{/* Edit modal */}
			<TaskConfirmationModal
				open={isModalOpen}
				onOpenChange={setIsModalOpen}
				instructions={taskInstructions}
				onSubmit={handleSubmitTask}
				isSubmitting={
					quickCreate.isPending ||
					updateTask.isPending ||
					createSchedule.isPending ||
					deleteSchedule.isPending
				}
				isEditMode={editingTaskId !== null}
				defaultName={defaultName}
				defaultIntegrations={defaultIntegrations}
				defaultGoals={defaultGoals}
				showInstructionsField={true}
				defaultSchedule={defaultSchedule}
				defaultPermissionMode={defaultPermissionMode}
				scheduleOnly={editingTaskId === null}
				hideEmailInboxSection={true}
			/>

			{/* Task run panel */}
			<TaskRunPanel
				open={isPanelOpen}
				onOpenChange={(open) => {
					if (!open) closePanel();
				}}
				task={allTasks?.find((t) => t.id === selectedTaskId) ?? null}
				onRunNow={handleRunTask}
				onEdit={handleEditTask}
				onSchedule={handleScheduleTask}
				latestRun={selectedTaskId ? latestRunByTaskId.get(selectedTaskId) : undefined}
				isRunning={runTask.isPending}
			/>
		</div>
	);
}
